import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Car, Train, Plane, Bus, MapPin, Clock, DollarSign, ExternalLink } from 'lucide-react';

type TransportType = 'flight' | 'train' | 'bus' | 'car' | 'taxi' | 'metro';

interface TransportOption {
  id: string;
  type: TransportType;
  name: string;
  description: string;
  estimatedCost: string;
  duration: string;
  pros: string[];
  cons: string[];
  bookingUrl?: string;
  icon: React.ReactNode;
}

interface TransportSuggestionsProps {
  destination?: string;
  destinationCountry?: string;
}

export function TransportSuggestions({ destination, destinationCountry }: TransportSuggestionsProps) {
  const [transportOptions, setTransportOptions] = useState<TransportOption[]>([]);
  const [loading, setLoading] = useState(false);

  const getTransportIcon = (type: TransportType) => {
    switch (type) {
      case 'flight': return <Plane className="h-4 w-4" />;
      case 'train': return <Train className="h-4 w-4" />;
      case 'bus': return <Bus className="h-4 w-4" />;
      case 'car': return <Car className="h-4 w-4" />;
      case 'taxi': return <Car className="h-4 w-4" />;
      case 'metro': return <Train className="h-4 w-4" />;
      default: return <MapPin className="h-4 w-4" />;
    }
  };

  // Mock transport data - in real app, this would come from APIs
  const getMockTransportData = (destination?: string, country?: string): TransportOption[] => {
    const baseOptions: TransportOption[] = [
      {
        id: '1',
        type: 'metro',
        name: 'Public Metro/Subway',
        description: 'Efficient urban rail transportation',
        estimatedCost: '$2-5 per ride',
        duration: 'Varies by route',
        pros: ['Cost-effective', 'Avoids traffic', 'Frequent service', 'Eco-friendly'],
        cons: ['Can be crowded', 'Limited coverage areas', 'May not run 24/7'],
        icon: getTransportIcon('metro')
      },
      {
        id: '2',
        type: 'taxi',
        name: 'Taxi/Ride-hailing',
        description: 'Door-to-door transportation service',
        estimatedCost: '$10-30 per trip',
        duration: '15-45 minutes',
        pros: ['Convenient', 'Direct routes', 'Available 24/7', 'No waiting for connections'],
        cons: ['More expensive', 'Traffic dependent', 'Surge pricing during peak times'],
        bookingUrl: 'https://uber.com',
        icon: getTransportIcon('taxi')
      },
      {
        id: '3',
        type: 'bus',
        name: 'Public Buses',
        description: 'Local and intercity bus services',
        estimatedCost: '$1-3 per ride',
        duration: 'Varies by route',
        pros: ['Very affordable', 'Extensive coverage', 'Good for sightseeing', 'Frequent service'],
        cons: ['Slower due to stops', 'Can be crowded', 'Limited luggage space'],
        icon: getTransportIcon('bus')
      },
      {
        id: '4',
        type: 'car',
        name: 'Car Rental',
        description: 'Rent a car for maximum flexibility',
        estimatedCost: '$30-80 per day',
        duration: 'At your pace',
        pros: ['Complete flexibility', 'Good for groups', 'Luggage space', 'Explore at own pace'],
        cons: ['Parking challenges', 'Traffic stress', 'Insurance needed', 'Fuel costs'],
        bookingUrl: 'https://booking.com/cars',
        icon: getTransportIcon('car')
      }
    ];

    // Add country-specific options
    if (country?.toLowerCase().includes('japan')) {
      baseOptions.unshift({
        id: '5',
        type: 'train',
        name: 'JR Pass (Japan Rail)',
        description: 'Unlimited travel on JR trains including Shinkansen',
        estimatedCost: '$280-520 for 7-21 days',
        duration: 'High-speed travel',
        pros: ['Unlimited travel', 'High-speed trains', 'Punctual', 'Comfortable'],
        cons: ['Upfront cost', 'Must buy before arrival', 'Not valid on all trains'],
        bookingUrl: 'https://japanrailpass.net',
        icon: getTransportIcon('train')
      });
    }

    if (country?.toLowerCase().includes('europe')) {
      baseOptions.unshift({
        id: '6',
        type: 'train',
        name: 'Eurail Pass',
        description: 'Flexible train travel across Europe',
        estimatedCost: '$200-600 for 15-90 days',
        duration: 'High-speed connections',
        pros: ['Multi-country access', 'High-speed trains', 'Central stations', 'Scenic routes'],
        cons: ['Reservation fees', 'Limited validity', 'Not always cheapest option'],
        bookingUrl: 'https://eurail.com',
        icon: getTransportIcon('train')
      });
    }

    return baseOptions;
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setTransportOptions(getMockTransportData(destination, destinationCountry));
      setLoading(false);
    }, 800);
  }, [destination, destinationCountry]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            Transport Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          Transport Suggestions
        </CardTitle>
        <CardDescription>
          {destination ? `Getting around ${destination}${destinationCountry ? `, ${destinationCountry}` : ''}` : 'Popular transportation options for your destination'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          {transportOptions.map((option) => (
            <Card key={option.id} className="border-l-4 border-l-primary/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      {option.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base">{option.name}</CardTitle>
                      <CardDescription className="text-sm">
                        {option.description}
                      </CardDescription>
                    </div>
                  </div>
                  {option.bookingUrl && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={option.bookingUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Book
                      </a>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span>{option.estimatedCost}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{option.duration}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <h5 className="font-medium text-green-700 mb-2">Pros</h5>
                    <ul className="text-sm space-y-1">
                      {option.pros.map((pro, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h5 className="font-medium text-orange-700 mb-2">Cons</h5>
                    <ul className="text-sm space-y-1">
                      {option.cons.map((con, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-900 mb-2">🚀 Pro Tips</h4>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• Download offline maps and transport apps before you go</li>
            <li>• Keep some local cash for transportation that doesn't accept cards</li>
            <li>• Check for tourist transport passes that offer better value</li>
            <li>• Consider peak hours when planning your travel times</li>
            <li>• Always validate tickets where required to avoid fines</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}